import {
  parsePhoneNumberFromString,
  isPossiblePhoneNumber,
} from "libphonenumber-js";

/** Matches Laravel super-admin `phone_number` => `max:20` */
export const ADMIN_PHONE_MAX_LEN = 20;

export function toE164(value, countryIso2) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  const iso2 = countryIso2 ? String(countryIso2).toUpperCase() : undefined;

  const phone = raw.startsWith("+")
    ? parsePhoneNumberFromString(raw)
    : parsePhoneNumberFromString(raw, iso2);

  if (!phone || !phone.isValid()) return null;
  return phone.number; // E.164, e.g. +491234567890
}

export function isValidPhone(value, countryIso2) {
  return Boolean(toE164(value, countryIso2));
}

export function phoneError(value, countryIso2) {
  const raw = String(value || "").trim();
  if (!raw) return "Phone number is required";
  if (!isValidPhone(raw, countryIso2)) return "Please enter a valid phone number.";
  return null;
}

/** +digits string capped at ADMIN_PHONE_MAX_LEN (leading +). */
export function clampAdminPhoneString(value) {
  const raw = String(value || "").trim();
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  const withPlus = `+${digits}`;
  if (withPlus.length <= ADMIN_PHONE_MAX_LEN) return withPlus;
  return `+${digits.slice(0, ADMIN_PHONE_MAX_LEN - 1)}`;
}

/**
 * Lenient E.164 for super-admin: prefer valid, then possible; else accept +digits within API length.
 */
export function toE164SuperAdmin(value, countryIso2) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  const strict = toE164(value, countryIso2);
  if (strict) return strict;

  const iso2 = countryIso2 ? String(countryIso2).toUpperCase() : undefined;
  const phone = raw.startsWith("+")
    ? parsePhoneNumberFromString(raw)
    : parsePhoneNumberFromString(raw, iso2);

  if (phone && phone.isPossible()) return phone.number;

  const clamped = clampAdminPhoneString(raw);
  if (
    clamped &&
    clamped.length <= ADMIN_PHONE_MAX_LEN &&
    isPossiblePhoneNumber(clamped)
  ) {
    return clamped;
  }

  const d = raw.replace(/\D/g, "");
  if (d.length >= 7 && d.length <= 19 && clamped.length <= ADMIN_PHONE_MAX_LEN) {
    return clamped;
  }

  return null;
}

export function isValidPhoneSuperAdmin(value, countryIso2) {
  return Boolean(toE164SuperAdmin(value, countryIso2));
}

export function phoneErrorSuperAdmin(value, countryIso2) {
  const raw = String(value || "").trim();
  if (!raw) return "Phone number is required";

  const clamped = clampAdminPhoneString(raw);
  if (!clamped) return "Please enter a valid phone number.";
  if (clamped.length > ADMIN_PHONE_MAX_LEN) {
    return `Phone number must be at most ${ADMIN_PHONE_MAX_LEN} characters (including +).`;
  }

  if (isPossiblePhoneNumber(clamped)) return null;

  const iso2 = countryIso2 ? String(countryIso2).toUpperCase() : undefined;
  const phone = raw.startsWith("+")
    ? parsePhoneNumberFromString(raw)
    : parsePhoneNumberFromString(raw, iso2);

  if (phone && phone.isPossible()) return null;

  const digits = raw.replace(/\D/g, "");
  if (digits.length >= 7 && clamped.length <= ADMIN_PHONE_MAX_LEN) return null;

  return "Please enter a valid phone number.";
}
